import React, { useEffect, useState, useRef } from 'react';
import { toast } from 'react-toastify';
import * as Checkbox from '@radix-ui/react-checkbox';
import { CheckIcon } from '@radix-ui/react-icons';
import styles from './AddIpo.module.css';
import FullscreenModal from '../../components/common/FullscreenModal/FullscreenModal';
import Button from '../../components/common/Button/Button';
import { REACT_APP_API_BASE_URL } from '../../apiurl';
import { getAllCorporateDeposits, createCorporateDeposit, updateCorporateDeposit, deleteCorporateDeposit, uploadCorporateDepositMedia } from '../../services/corporateDepositService';
import { validateInvestmentForm } from './validation';
import FormField from './FormField';
import LogoField from './LogoField';
import { formatAdminDate } from '../../utils/datetime';

const EditIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
);
const TrashIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4h6v2" />
    </svg>
);

const initialFormData = {
    company_name: '', logo: '',
    start_date: '', close_date: '', listing_date: '',
    min_investment: '', max_investment: '',
    return_1year: '', return_2year: '', return_3year: '',
    return_4year: '', return_5year: '', return_allyear: '',
    rating: '', notes: '', company_website: '',
    active_status: true, accept_applications: true
};

const FixedDepositPage = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);
    const [formData, setFormData] = useState(initialFormData);
    const [errors, setErrors] = useState({});
    const [proposalFiles, setProposalFiles] = useState([]);
    const [newMediaLabel, setNewMediaLabel] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const baselineRef = useRef('');

    useEffect(() => { fetchItems(); }, []);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const res = await getAllCorporateDeposits();
            const data = res?.data || [];
            setItems(Array.isArray(data) ? data : []);
        } catch (err) {
            toast.error(err?.message || "Failed to fetch Fixed Deposits");
        } finally { setLoading(false); }
    };

    const filtered = items.filter(i => i.company_name?.toLowerCase().includes(searchTerm.toLowerCase()));

    const openAddModal = () => {
        setIsEditing(false); setEditId(null);
        setFormData(initialFormData); setErrors({}); setProposalFiles([]);
        baselineRef.current = JSON.stringify({ fd: initialFormData, files: [] });
        setIsModalOpen(true);
    };

    const openEditModal = (item) => {
        setIsEditing(true); setEditId(item.id);
        const fmtDate = (d) => { if (!d) return ''; const dt = new Date(d); return isNaN(dt) ? '' : dt.toISOString().split('T')[0]; };
        const fd = {
            company_name: item.company_name || '', logo: item.logo || '',
            start_date: fmtDate(item.start_date), close_date: fmtDate(item.close_date), listing_date: fmtDate(item.listing_date),
            min_investment: item.min_investment ?? '', max_investment: item.max_investment ?? '',
            return_1year: item.return_1year ?? '', return_2year: item.return_2year ?? '',
            return_3year: item.return_3year ?? '', return_4year: item.return_4year ?? '',
            return_5year: item.return_5year ?? '', return_allyear: item.return_allyear ?? '',
            rating: item.rating || '', notes: item.notes || '', company_website: item.company_website || '',
            active_status: item.active_status !== false, accept_applications: item.accept_applications !== false,
        };
        const files = Array.isArray(item.proposal_files) ? item.proposal_files : [];
        setFormData(fd); setErrors({}); setProposalFiles(files);
        baselineRef.current = JSON.stringify({ fd, files });
        setIsModalOpen(true);
    };

    const closeModal = () => { setIsModalOpen(false); setNewMediaLabel(''); };
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        // Clear this field's message as soon as it is edited; the rest stay
        // until the next save so the user still sees what is outstanding.
        setErrors(prev => (prev[name] ? { ...prev, [name]: undefined } : prev));
    };
    const isDirty = JSON.stringify({ fd: formData, files: proposalFiles }) !== baselineRef.current;

    const handleSave = async () => {
        const validationErrors = validateInvestmentForm('FIXED_DEPOSIT', formData);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            toast.error("Please correct the highlighted fields");
            return;
        }
        setErrors({});
        if (saving) return;
        const payload = { ...formData, proposal_files: proposalFiles };
        setSaving(true);
        try {
            if (isEditing) { await updateCorporateDeposit(editId, payload); toast.success("Fixed Deposit updated"); }
            else { await createCorporateDeposit(payload); toast.success("Fixed Deposit created"); }
            closeModal(); fetchItems();
        } catch (err) { toast.error(err?.message || "Failed to save"); }
        finally { setSaving(false); }
    };

    // Opens the confirm dialog; the delete itself runs from confirmDelete.
    const handleDelete = (id) => setDeleteId(id);

    const confirmDelete = async () => {
        if (deleteId == null) return;
        setDeleting(true);
        try {
            await deleteCorporateDeposit(deleteId);
            toast.success("Fixed Deposit deleted");
            setDeleteId(null);
            fetchItems();
        } catch (err) {
            toast.error(err?.message || "Failed to delete");
        } finally {
            setDeleting(false);
        }
    };

    const MAX_FILE_SIZE = 20 * 1024 * 1024;
    const handleMediaUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !newMediaLabel.trim()) return;
        if (file.size > MAX_FILE_SIZE) { toast.error(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 20 MB.`); e.target.value = ""; return; }
        const fd = new FormData(); fd.append("file", file);
        setUploading(true);
        try {
            const res = await uploadCorporateDepositMedia(fd);
            const { url, type } = res?.data || {};
            if (!url) { toast.error("Upload failed"); return; }
            setProposalFiles(prev => [...prev, { label: newMediaLabel.trim(), url, type: type || (file.type.includes('image') ? 'image' : 'pdf') }]);
            toast.success("File uploaded"); setNewMediaLabel('');
        } catch (err) { toast.error(err?.message || "Upload failed"); }
        finally { setUploading(false); e.target.value = ""; }
    };
    // Uploads a logo through the same media endpoint and stores the returned
    // path in formData.logo, so the field can still be typed if preferred.
    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > MAX_FILE_SIZE) {
            toast.error(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum is 20 MB.`);
            e.target.value = "";
            return;
        }

        const fd = new FormData();
        fd.append("file", file);

        setUploadingLogo(true);
        try {
            const res = await uploadCorporateDepositMedia(fd);
            const { url } = res?.data || {};
            if (!url) {
                toast.error("Logo upload failed - no URL returned");
                return;
            }
            setFormData(prev => ({ ...prev, logo: url }));
            setErrors(prev => ({ ...prev, logo: undefined }));
            toast.success("Logo uploaded");
        } catch (err) {
            toast.error(err?.message || "Logo upload failed");
        } finally {
            setUploadingLogo(false);
            e.target.value = "";
        }
    };

    const removeMedia = (i) => setProposalFiles(prev => prev.filter((_, idx) => idx !== i));
    const getPreviewUrl = (url) => {
        const v = String(url || '').trim();
        if (!v) return '';
        if (/^https?:\/\//i.test(v)) return v;
        return `${REACT_APP_API_BASE_URL}${v.replace(/^\//, '')}`;
    };

    return (
        <div className={styles.pageContainer}>
            <div className={styles.pageHeader}>
                <h1 className={styles.pageTitle}>Fixed Deposit Management</h1>
                <Button onClick={openAddModal}>Add New FD</Button>
            </div>
            <div className={styles.controlsRow}>
                <div className={styles.searchBox}>
                    <input type="text" placeholder="Search by Company Name..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className={styles.searchInput} />
                </div>
            </div>
            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Company</th>
                            <th>Deposit Range</th>
                            <th>1Y</th><th>3Y</th><th>5Y</th>
                            <th>Rating</th>
                            <th>Open</th><th>Close</th>
                            <th>Status</th><th>Files</th><th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map(item => (
                            <tr key={item.id}>
                                <td>{item.company_name}</td>
                                <td>{item.min_investment} - {item.max_investment}</td>
                                <td>{item.return_1year ?? '-'}%</td>
                                <td>{item.return_3year ?? '-'}%</td>
                                <td>{item.return_5year ?? '-'}%</td>
                                <td>{item.rating || '-'}</td>
                                <td>{formatAdminDate(item.start_date, '-')}</td>
                                <td>{formatAdminDate(item.close_date, '-')}</td>
                                <td><span className={`${styles.badge} ${item.active_status ? styles.active : styles.inactive}`}>{item.active_status ? 'Active' : 'Inactive'}</span></td>
                                <td>{Array.isArray(item.proposal_files) ? item.proposal_files.length : 0}</td>
                                <td>
                                    <div className={styles.actions}>
                                        <button className={styles.actionBtn} onClick={() => openEditModal(item)} title="Edit"><EditIcon /></button>
                                        <button className={`${styles.actionBtn} ${styles.deleteBtn}`} onClick={() => handleDelete(item.id)} title="Delete"><TrashIcon /></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {filtered.length === 0 && <tr><td colSpan="11" style={{textAlign:'center',padding:'30px'}}>{loading ? 'Loading...' : 'No Fixed Deposits found'}</td></tr>}
                    </tbody>
                </table>
            </div>

            {isModalOpen && (
                <FullscreenModal isOpen={isModalOpen} onClose={closeModal} title={isEditing ? "Edit Fixed Deposit" : "Add Fixed Deposit"} size="fullscreen" maxWidth="1000px" contentHeight="82vh" disableBackdropClick={true}>
                    <div className={styles.modalContent}>
                        <div className={styles.formGrid}>
                            <FormField label="Company Name" name="company_name" required value={formData.company_name} onChange={handleChange} error={errors.company_name} />
                            <LogoField value={formData.logo} onChange={handleChange} error={errors.logo} uploading={uploadingLogo} onUpload={handleLogoUpload} previewUrl={getPreviewUrl(formData.logo)} />
                            <FormField label="Start Date" name="start_date" type="date" value={formData.start_date} onChange={handleChange} error={errors.start_date} />
                            <FormField label="Close Date" name="close_date" type="date" value={formData.close_date} onChange={handleChange} error={errors.close_date} />
                            <FormField label="Listing Date" name="listing_date" type="date" value={formData.listing_date} onChange={handleChange} error={errors.listing_date} />
                            <FormField label="Min Deposit" name="min_investment" type="number" step="0.01" value={formData.min_investment} onChange={handleChange} error={errors.min_investment} />
                            <FormField label="Max Deposit" name="max_investment" type="number" step="0.01" value={formData.max_investment} onChange={handleChange} error={errors.max_investment} />
                            <FormField label="1 Year Return (%)" name="return_1year" type="number" step="0.01" value={formData.return_1year} onChange={handleChange} error={errors.return_1year} />
                            <FormField label="2 Year Return (%)" name="return_2year" type="number" step="0.01" value={formData.return_2year} onChange={handleChange} error={errors.return_2year} />
                            <FormField label="3 Year Return (%)" name="return_3year" type="number" step="0.01" value={formData.return_3year} onChange={handleChange} error={errors.return_3year} />
                            <FormField label="4 Year Return (%)" name="return_4year" type="number" step="0.01" value={formData.return_4year} onChange={handleChange} error={errors.return_4year} />
                            <FormField label="5 Year Return (%)" name="return_5year" type="number" step="0.01" value={formData.return_5year} onChange={handleChange} error={errors.return_5year} />
                            <FormField label="All Year Return (%)" name="return_allyear" type="number" step="0.01" value={formData.return_allyear} onChange={handleChange} error={errors.return_allyear} />
                            <FormField label="Rating" name="rating" value={formData.rating} onChange={handleChange} error={errors.rating} placeholder="e.g. Crisil AA+" />
                            <FormField label="Company Website" name="company_website" value={formData.company_website} onChange={handleChange} error={errors.company_website} placeholder="https://…" />
                            <FormField label="Notes" name="notes" textarea fullWidth value={formData.notes} onChange={handleChange} error={errors.notes} />
                            <div className={styles.formGroup}>
                                <label className={styles.label}>Active Status</label>
                                <div className={styles.checkboxRow}>
                                    <Checkbox.Root className={styles.checkboxRoot} checked={formData.active_status} onCheckedChange={(c) => setFormData(p => ({...p, active_status: !!c}))}><Checkbox.Indicator className={styles.checkboxIndicator}><CheckIcon /></Checkbox.Indicator></Checkbox.Root>
                                    <span className={styles.checkboxText}>{formData.active_status ? 'Active' : 'Inactive'}</span>
                                </div>
                            </div>
                            <div className={styles.formGroup}>
                                <label className={styles.label}>Accept Applications</label>
                                <div className={styles.checkboxRow}>
                                    <Checkbox.Root className={styles.checkboxRoot} checked={formData.accept_applications} onCheckedChange={(c) => setFormData(p => ({...p, accept_applications: !!c}))}><Checkbox.Indicator className={styles.checkboxIndicator}><CheckIcon /></Checkbox.Indicator></Checkbox.Root>
                                    <span className={styles.checkboxText}>{formData.accept_applications ? 'Accepting' : 'Not Accepting'}</span>
                                </div>
                            </div>
                        </div>
                        <div className={styles.mediaSection}>
                            <h3 className={styles.sectionTitle}>Proposal Files</h3>
                            <div className={styles.addMediaRow}>
                                <div className={styles.formGroup} style={{flex:'0 0 320px'}}><label className={styles.label}>Media Label</label><input type="text" value={newMediaLabel} onChange={e => setNewMediaLabel(e.target.value)} className={styles.input} placeholder="Enter label (e.g. Prospectus)" /></div>
                                <div className={styles.uploadGroup}><span className={styles.groupLabel}>Upload</span><div className={styles.groupBtns}><label className={`${styles.uploadBtn} ${!newMediaLabel.trim() ? styles.uploadBtnDisabled : ''}`}><svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.81825 1.18188C7.64251 1.00615 7.35759 1.00615 7.18185 1.18188L4.18185 4.18188C4.00611 4.35762 4.00611 4.64254 4.18185 4.81828C4.35759 4.99401 4.64251 4.99401 4.81825 4.81828L7.05005 2.58648V9.49996C7.05005 9.74849 7.25152 9.94996 7.50005 9.94996C7.74858 9.94996 7.95005 9.74849 7.95005 9.49996V2.58648L10.1819 4.81828C10.3576 4.99401 10.6425 4.99401 10.8182 4.81828C10.994 4.64254 10.994 4.35762 10.8182 4.18188L7.81825 1.18188ZM2.5 9.99997C2.77614 9.99997 3 10.2238 3 10.5V12C3 12.5538 3.44565 13 3.99635 13H11.0012C11.5529 13 12 12.5528 12 12V10.5C12 10.2238 12.2239 9.99997 12.5 9.99997C12.7761 9.99997 13 10.2238 13 10.5V12C13 13.104 12.1062 14 11.0012 14H3.99635C2.89019 14 2 13.103 2 12V10.5C2 10.2238 2.22386 9.99997 2.5 9.99997Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path></svg>{uploading ? 'Uploading…' : 'Upload Media'}<input type="file" accept="image/png,image/jpeg,image/webp,image/gif,application/pdf" onChange={handleMediaUpload} disabled={uploading || !newMediaLabel.trim()} hidden /></label></div></div>
                            </div>
                            <div className={styles.mediaList}>
                                {proposalFiles.map((file, idx) => (
                                    <div key={idx} className={styles.mediaItem}>
                                        <div className={styles.mediaPreview}>{file.type === 'pdf' || file.url?.endsWith('.pdf') ? <span className={styles.mediaPreviewIcon}>📄</span> : <img src={getPreviewUrl(file.url)} alt={file.label} onError={e => e.target.style.display='none'} />}</div>
                                        <div className={styles.mediaInfo}><div className={styles.mediaLabel}>{file.label}</div><div className={styles.mediaUrl}>{file.url}</div></div>
                                        <button className={`${styles.actionBtn} ${styles.deleteBtn}`} onClick={() => removeMedia(idx)} title="Remove"><TrashIcon /></button>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className={styles.modalActions}>
                            <Button variant="secondary" onClick={closeModal}>Cancel</Button>
                            <Button variant="primary" onClick={handleSave} disabled={!isDirty || saving}>{saving ? 'Saving...' : 'Save FD'}</Button>
                        </div>
                    </div>
                </FullscreenModal>
            )}

            {deleteId !== null && (
                <div
                    className={styles.confirmOverlay}
                    onClick={() => { if (!deleting) setDeleteId(null); }}
                >
                    <div className={styles.confirmCard} onClick={(e) => e.stopPropagation()}>
                        <h2 className={styles.confirmTitle}>Delete Fixed Deposit</h2>
                        <p className={styles.confirmText}>
                            Are you sure you want to delete this fixed deposit? This action cannot be undone.
                        </p>
                        <div className={styles.confirmActions}>
                            <Button variant="secondary" onClick={() => setDeleteId(null)} disabled={deleting}>
                                Cancel
                            </Button>
                            <Button variant="danger" onClick={confirmDelete} loading={deleting} disabled={deleting}>
                                Delete
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FixedDepositPage;
